/**
 * ADR 115 Phase 4 — honors the backend's `redirect_url` so a
 * tenant that signs in (or is sitting) on the wrong brand host lands on
 * the brand-correct one.
 *
 * Mounted once inside the auth provider tree (see `app/App.tsx`). Handles
 * the two Phase-4 surfaces with deliberately different UX:
 *
 *   - **Login** → immediate navigation. When the just-authenticated user
 *     object carries a `redirect_url` (the login response field, spread
 *     onto the user by `@aegix/auth-identity`'s `unpackLoginResult`), we
 *     `window.location.assign(...)` right away. There's no in-progress
 *     work at sign-in, so a silent host switch is the right call.
 *
 *   - **Refresh** → graceful interstitial. A mid-session brand flip
 *     (surfaced via `onRefreshRedirect`, published from the sms-web token
 *     refresh path) must NOT yank an admin out of an active screen. We
 *     show a dialog explaining the switch and let them choose when to go.
 *
 * The access session cookie is host-only and per-app-named
 * (`jwt-auth-sms`, ADR-118 Phase 2), so it does
 * NOT follow the user across hosts. The session survives the host change
 * via the HttpOnly parent-domain `aegix-sso` assertion, consumed at boot
 * on the target host by the auth lib's SSO bootstrap; this
 * component never clears storage/cookies.
 *
 * Renders nothing in the steady state — only the refresh interstitial
 * dialog when a mid-session redirect is pending.
 */

import { useEffect, useRef, useState } from 'react';
import { Dialog, Portal, Text, VStack } from '@chakra-ui/react';
import { Button as ButtonComponent } from '@themes/aegix';
import { useIsAuthenticated, useUser } from '@aegix/auth-identity';
import type { SMSUser } from '../lib/sms-auth';
import {
  getRedirectUrl,
  onRefreshRedirect,
  performBrandRedirect,
} from '../lib/brandRedirect';

export function BrandRedirectGate() {
  const isAuthenticated = useIsAuthenticated();
  const user = useUser<SMSUser>();

  // Latches once we've fired (or are about to fire) the login-time
  // navigation, so a re-render before the browser tears down the JS
  // context doesn't double-assign. `window.location.assign` is async-ish
  // (the navigation settles after the current task), so without this guard
  // the effect could re-run and re-assign.
  const loginRedirectFired = useRef(false);

  // Refresh-flow interstitial target. `null` = no pending mid-session
  // redirect (dialog closed).
  const [pendingRefreshRedirect, setPendingRefreshRedirect] = useState<
    string | null
  >(null);

  // --- Login flow: navigate immediately on a present-and-different URL. ---
  useEffect(() => {
    if (!isAuthenticated || !user) return;
    if (loginRedirectFired.current) return;

    const redirectUrl = getRedirectUrl(user);
    if (!redirectUrl) return;

    // performBrandRedirect no-ops (returns false) for same-origin /
    // malformed targets, so a `redirect_url` echoing our own host won't
    // loop. Latch regardless of the return so we don't re-evaluate every
    // render once we've made the decision for this session.
    loginRedirectFired.current = true;
    performBrandRedirect(redirectUrl);
  }, [isAuthenticated, user]);

  // --- Refresh flow: surface an interstitial; never auto-navigate. ---
  useEffect(() => {
    return onRefreshRedirect((redirectUrl) => {
      setPendingRefreshRedirect((current) => current ?? redirectUrl);
    });
  }, []);

  const handleContinue = () => {
    if (!pendingRefreshRedirect) return;
    performBrandRedirect(pendingRefreshRedirect);
    // If the redirect is a no-op (shouldn't happen — publishRefreshRedirect
    // pre-filters via shouldRedirect), close the dialog so we don't strand
    // a dead prompt on screen.
    setPendingRefreshRedirect(null);
  };

  const handleDismiss = () => {
    // User chose to stay; drop the prompt. A later refresh that still
    // mismatches will re-publish and re-open it.
    setPendingRefreshRedirect(null);
  };

  return (
    <Dialog.Root
      open={pendingRefreshRedirect !== null}
      onOpenChange={({ open }) => {
        if (!open) handleDismiss();
      }}
      role="alertdialog"
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content data-e2e="brand-redirect-interstitial">
            <Dialog.Header>
              <Dialog.Title>Switch to your organization's site</Dialog.Title>
            </Dialog.Header>
            <Dialog.Body>
              <VStack align="stretch" gap={3}>
                <Text fontSize="sm" color="text.primary">
                  Your account is set up on a different Aegix site. You can
                  switch now to continue on the correct one — you'll stay signed
                  in.
                </Text>
                <Text fontSize="sm" color="text.secondary">
                  You can keep working here for now and switch later if you
                  prefer.
                </Text>
              </VStack>
            </Dialog.Body>
            <Dialog.Footer>
              <ButtonComponent
                visual="secondary"
                onClick={handleDismiss}
                data-e2e="brand-redirect-stay"
              >
                Not now
              </ButtonComponent>
              <ButtonComponent
                visual="primary"
                onClick={handleContinue}
                data-e2e="brand-redirect-continue"
              >
                Switch now
              </ButtonComponent>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
}
